import React, { createContext, useReducer, useCallback } from 'react';
import type { ValidationStatus } from '@fiverr-private/store_builder_types';
import { stepValidationReducer } from '../../reducers/step-validation/StepValidationReducer';

interface StepValidationContextType {
    registerStepValidation: (stepId: string, validationStatusFn: () => ValidationStatus) => void;
    registerValidationOnNext: (stepId: string, validationStatusFn: () => Promise<ValidationStatus>) => void;
    unregisterStepValidation: (stepId: string) => void;
    unregisterValidationOnNext: (stepId: string) => void;
    stepValidationStatus: (stepId: string) => ValidationStatus;
    stepValidationOnNextStatus: (stepId: string) => Promise<ValidationStatus>;
}

export const StepValidationContext = createContext<StepValidationContextType | undefined>(undefined);

interface StepValidationProviderProps extends React.PropsWithChildren<{}> {}
export const StepValidationProvider = ({ children }: StepValidationProviderProps) => {
    const [state, dispatch] = useReducer(stepValidationReducer, {
        validations: {},
        validationsAfterContinue: {},
    });

    const registerStepValidation = useCallback((stepId: string, validationStatusFn: () => ValidationStatus) => {
        dispatch({ type: 'REGISTER_VALIDATION', stepId, validationStatusFn });
    }, []);

    const registerValidationOnNext = useCallback(
        (stepId: string, validationStatusFn: () => Promise<ValidationStatus>) => {
            dispatch({ type: 'REGISTER_VALIDATION_AFTER_CONTINUE', stepId, validationStatusFn });
        },
        []
    );

    const unregisterStepValidation = useCallback((stepId: string) => {
        dispatch({ type: 'UNREGISTER_VALIDATION', stepId });
    }, []);

    const unregisterValidationOnNext = useCallback((stepId: string) => {
        dispatch({ type: 'UNREGISTER_VALIDATION_AFTER_CONTINUE', stepId });
    }, []);

    const stepValidationStatus = useCallback(
        (stepId: string) => {
            const validationStatusFn = state.validations[stepId];
            return validationStatusFn ? validationStatusFn() : 'valid';
        },
        [state.validations]
    );

    const stepValidationOnNextStatus = useCallback(
        async (stepId: string) => {
            const validationStatusFn = state.validationsAfterContinue[stepId];
            if (!validationStatusFn) {
                return 'valid';
            }
            try {
                const validationStatus = await validationStatusFn();
                return validationStatus;
            } catch (error) {
                return 'invalid';
            }
        },
        [state.validationsAfterContinue]
    );

    return (
        <StepValidationContext.Provider
            value={{
                registerStepValidation,
                registerValidationOnNext,
                unregisterStepValidation,
                unregisterValidationOnNext,
                stepValidationStatus,
                stepValidationOnNextStatus,
            }}
        >
            {children}
        </StepValidationContext.Provider>
    );
};
